import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import MaterialSelectionModal from './MaterialSelectionModal';
import EquipmentSelectionModal from './EquipmentSelectionModal';
import { useAuth } from '../../../contexts/AuthContext';
import nodeLibraryService from '../../../services/nodeLibraryService';

const PropertiesPanel = ({ selectedNode, onNodeUpdate, onNodeDelete, isCollapsed, onToggleCollapse }) => {
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);
  const { user } = useAuth();
  
  // Node type options from Node Library Management
  const [equipmentNodeTypes, setEquipmentNodeTypes] = useState([]);
  const [laborNodeTypes, setLaborNodeTypes] = useState([]);
  const [materialsNodeTypes, setMaterialsNodeTypes] = useState([]);
  const [loadingNodeTypes, setLoadingNodeTypes] = useState(false);

  // Load node types from Node Library Management when component mounts
  useEffect(() => {
    loadNodeTypesFromLibrary();
  }, []);

  const loadNodeTypesFromLibrary = async () => {
    try {
      setLoadingNodeTypes(true);
      const allNodes = await nodeLibraryService?.getAllNodes();
      
      // Filter nodes by node_type
      const equipmentNodes = allNodes?.filter(node => node?.node_type === 'equipment');
      const laborNodes = allNodes?.filter(node => node?.node_type === 'labor');
      const materialsNodes = allNodes?.filter(node => node?.node_type === 'materials');
      
      // Format for Select component
      setEquipmentNodeTypes([
        { value: '', label: 'Select Equipment Type' },
        ...equipmentNodes?.map(node => ({
          value: node?.id,
          label: node?.name,
          data: node
        }))
      ]);
      
      setLaborNodeTypes([
        { value: '', label: 'Select Labor Type' },
        ...laborNodes?.map(node => ({
          value: node?.id,
          label: node?.name,
          data: node
        }))
      ]);
      
      setMaterialsNodeTypes([
        { value: '', label: 'Select Materials Type' },
        ...materialsNodes?.map(node => ({
          value: node?.id,
          label: node?.name,
          data: node
        }))
      ]);
    } catch (err) {
      console.error('Error loading node types from library:', err);
    } finally {
      setLoadingNodeTypes(false);
    }
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'started', label: 'Started' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleFieldChange = (field, value) => {
    if (selectedNode && onNodeUpdate) {
      const updatedNode = {
        ...selectedNode,
        data: {
          ...selectedNode?.data,
          [field]: value,
          lastSaved: Date.now() // Add timestamp for visual confirmation
        }
      };
      onNodeUpdate(selectedNode?.id, updatedNode);
      
      // Clear the lastSaved badge after 3 seconds
      setTimeout(() => {
        if (selectedNode && onNodeUpdate) {
          onNodeUpdate(selectedNode?.id, {
            ...updatedNode,
            data: {
              ...updatedNode?.data,
              lastSaved: null
            }
          });
        }
      }, 3000);
    }
  };

  const handleDeleteNode = (e) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation();
    }
    if (selectedNode?.id && onNodeDelete) {
      if (window.confirm(`Delete node "${selectedNode?.name || 'Unnamed Node'}"?`)) {
        onNodeDelete(selectedNode?.id);
      }
    }
  };

  const handleMaterialSelect = (material) => {
    if (selectedNode && onNodeUpdate) {
      onNodeUpdate(selectedNode?.id, {
        ...selectedNode,
        data: {
          ...selectedNode?.data,
          materialId: material?.id,
          materialName: material?.name,
          materialAttributes: material?.materialAttributes || '',
          costPerUnit: material?.costPerUnit,
          unit: material?.unit
        }
      });
    }
  };

  const handleEquipmentSelect = (equipment) => {
    if (selectedNode && onNodeUpdate) {
      onNodeUpdate(selectedNode?.id, {
        ...selectedNode,
        data: {
          ...selectedNode?.data,
          equipmentId: equipment?.id,
          equipmentName: equipment?.name,
          equipmentType: equipment?.equipmentType,
          powerRequirements: equipment?.powerRequirements,
          costPerUnit: equipment?.costPerUnit,
          unitOfMeasure: equipment?.unitOfMeasure,
          availabilityStatus: equipment?.availabilityStatus,
          availabilityCalendar: equipment?.availabilityCalendar || []
        }
      });
    }
  };

  const handleAddBooking = () => {
    const newBooking = {
      date: new Date()?.toISOString()?.split('T')?.[0],
      time: '09:00-17:00',
      bookedBy: user?.email || 'Unknown User'
    };
    
    const currentCalendar = selectedNode?.data?.availabilityCalendar || [];
    handleFieldChange('availabilityCalendar', [...currentCalendar, newBooking]);
  };

  const handleRemoveBooking = (index) => {
    const currentCalendar = selectedNode?.data?.availabilityCalendar || [];
    const updatedCalendar = currentCalendar?.filter((_, i) => i !== index);
    handleFieldChange('availabilityCalendar', updatedCalendar);
  };

  // STRENGTHENED: Differentiate between Equipment, Labor, and Material nodes with comprehensive checks
  const isMaterialNode =
    selectedNode?.node_type === 'materials' ||
    selectedNode?.type === 'resource-material'|| (selectedNode?.type?.toLowerCase()?.includes('material') && selectedNode?.category === 'resources') ||
    selectedNode?.name?.toLowerCase()?.includes('material');

  const isEquipmentNode =
    selectedNode?.node_type === 'equipment' ||
    selectedNode?.type === 'resource-equipment'|| (selectedNode?.type?.toLowerCase()?.includes('equipment') && selectedNode?.category === 'resources');

  const isLaborNode =
    selectedNode?.node_type === 'labor' ||
    selectedNode?.node_type === 'labour' ||
    selectedNode?.node_type === 'labour_'|| selectedNode?.node_type?.toLowerCase()?.includes('labor') ||
    selectedNode?.node_type?.toLowerCase()?.includes('labour') ||
    selectedNode?.type === 'resource-labor' ||
    selectedNode?.id === 'resource-labor' ||
    (selectedNode?.category === 'resources' && (selectedNode?.name?.toLowerCase()?.includes('labor') || selectedNode?.name?.toLowerCase()?.includes('labour'))) ||
    (selectedNode?.type?.toLowerCase()?.includes('labor') && selectedNode?.category === 'resources');

  // Detect Base Task nodes
  const isBaseTaskNode =
    selectedNode?.node_type === 'task' ||
    selectedNode?.type === 'task' ||
    selectedNode?.category === 'tasks'|| (selectedNode?.type?.toLowerCase()?.includes('task') && !isLaborNode && !isMaterialNode && !isEquipmentNode);

  // Debug logging to help diagnose Labor node detection
  useEffect(() => {
    if (selectedNode) {
      console.log('=== PropertiesPanel Debug ===');
      console.log('Selected Node:', selectedNode);
      console.log('node_type:', selectedNode?.node_type);
      console.log('type:', selectedNode?.type);
      console.log('category:', selectedNode?.category);
      console.log('name:', selectedNode?.name);
      console.log('isMaterialNode:', isMaterialNode);
      console.log('isEquipmentNode:', isEquipmentNode);
      console.log('isLaborNode:', isLaborNode);
      console.log('isBaseTaskNode:', isBaseTaskNode);
      console.log('===========================');
    }
  }, [selectedNode, isMaterialNode, isEquipmentNode, isLaborNode, isBaseTaskNode]);

  if (isCollapsed) {
    return (
      <div className="w-16 h-full bg-card border-l border-border flex flex-col items-center py-4">
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 transition-colors duration-250"
          aria-label="Expand properties panel"
        >
          <Icon name="ChevronLeft" size={20} />
        </button>
      </div>
    );
  }

  if (!selectedNode) {
    return (
      <div className="w-full h-full bg-card border-l border-border flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Properties</h2>
          <button
            onClick={onToggleCollapse}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
            aria-label="Collapse properties panel"
          >
            <Icon name="ChevronRight" size={18} />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <Icon name="MousePointerClick" size={48} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-sm text-muted-foreground">
              Select a node to view properties
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-card border-l border-border flex flex-col">
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">Properties</h2>
        <button
          onClick={onToggleCollapse}
          className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted hover:bg-muted/80 transition-colors duration-250"
          aria-label="Collapse properties panel"
        >
          <Icon name="ChevronRight" size={18} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-6">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0"
            style={{ backgroundColor: `${selectedNode?.color}20`, color: selectedNode?.color }}
          >
            <Icon name={selectedNode?.icon} size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-foreground">{selectedNode?.name}</h3>
            <p className="caption text-muted-foreground">{selectedNode?.type}</p>
          </div>
        </div>

        <div className="space-y-4">
          {isMaterialNode ? (
            // Material-specific properties (links to Material Management Database)
            (<>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Node Name</label>
                <Input
                  value={selectedNode?.name || ''}
                  onChange={(e) => {
                    onNodeUpdate(selectedNode?.id, {
                      ...selectedNode,
                      name: e?.target?.value
                    });
                  }}
                  placeholder="Enter material node name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">UID</label>
                <Input
                  value={selectedNode?.data?.uid || selectedNode?.id || ''}
                  placeholder="Auto-generated unique identifier"
                  disabled
                  className="bg-muted/50 cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto generated</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Material Selection</label>
                <div className="space-y-2">
                  {selectedNode?.data?.materialName && (
                    <div className="p-3 bg-muted rounded-lg space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{selectedNode?.data?.materialName}</p>
                          <p className="text-xs text-muted-foreground">ID: {selectedNode?.data?.materialId}</p>
                        </div>
                        <Icon name="Package" size={16} color="var(--color-primary)" />
                      </div>
                      {selectedNode?.data?.materialAttributes && (
                        <div className="pt-2 border-t border-border">
                          <p className="text-xs font-medium text-foreground mb-1">Material Attributes:</p>
                          <p className="text-xs text-muted-foreground">{selectedNode?.data?.materialAttributes}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setIsMaterialModalOpen(true)}
                  >
                    <Icon name="Search" size={16} className="mr-2" />
                    {selectedNode?.data?.materialName ? 'Change Material' : 'Search Material via Categories'}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    QTY
                    {selectedNode?.data?.unit && (
                      <span className="text-muted-foreground font-normal"> ({selectedNode?.data?.unit})</span>
                    )}
                  </label>
                  <Input
                    type="number"
                    value={selectedNode?.data?.quantity || 0}
                    onChange={(e) => handleFieldChange('quantity', parseFloat(e?.target?.value) || 0)}
                    placeholder="0"
                    min="0"
                    step="0.1"
                  />
                  {selectedNode?.data?.unit && (
                    <p className="text-xs text-muted-foreground mt-1">Unit: {selectedNode?.data?.unit}</p>
                  )}
                  {selectedNode?.data?.materialId && (
                    <p className="text-xs text-blue-600 mt-1">Auto generated</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Unit Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      value={selectedNode?.data?.costPerUnit || 0}
                      onChange={(e) => handleFieldChange('costPerUnit', parseFloat(e?.target?.value) || 0)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="pl-7"
                      disabled={selectedNode?.data?.materialId ? true : false}
                    />
                  </div>
                  {selectedNode?.data?.unit && (
                    <p className="text-xs text-muted-foreground mt-1">per {selectedNode?.data?.unit}</p>
                  )}
                  {selectedNode?.data?.materialId && (
                    <p className="text-xs text-blue-600 mt-1">Auto generated</p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={selectedNode?.data?.status || 'pending'}
                  onChange={(value) => handleFieldChange('status', value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">General Node</label>
                <Input
                  value={selectedNode?.data?.generalNode || ''}
                  onChange={(e) => handleFieldChange('generalNode', e?.target?.value)}
                  placeholder="General node information"
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created by {selectedNode?.data?.createdBy || user?.email || 'Unknown User'}</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <Checkbox
                  id="container-mode"
                  checked={selectedNode?.data?.isContainer || false}
                  onChange={(checked) => handleFieldChange('isContainer', checked)}
                />
                <label htmlFor="container-mode" className="text-sm text-foreground cursor-pointer flex-1">
                  Container Node
                </label>
                <Icon name="Box" size={16} color="var(--color-muted-foreground)" />
              </div>
            </>)
          ) : isEquipmentNode ? (
            // Equipment-specific properties (links to Node Library Management - Equipment Node Types)
            (<>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Node Name</label>
                <Input
                  value={selectedNode?.name || ''}
                  onChange={(e) => {
                    onNodeUpdate(selectedNode?.id, {
                      ...selectedNode,
                      name: e?.target?.value
                    });
                  }}
                  placeholder="Enter equipment node name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">UID</label>
                <Input
                  value={selectedNode?.data?.uid || selectedNode?.id || ''}
                  placeholder="Auto-generated unique identifier"
                  disabled
                  className="bg-muted/50 cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto generated</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Equipment Node Type</label>
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Cpu" size={16} color="var(--color-primary)" />
                    <p className="text-xs font-medium text-foreground">Link to Node Library Management</p>
                  </div>
                  <Select
                    value={selectedNode?.data?.linkedNodeTypeId || ''}
                    onChange={(value) => {
                      const selectedNodeType = equipmentNodeTypes?.find(nt => nt?.value === value);
                      handleFieldChange('linkedNodeTypeId', value);
                      handleFieldChange('linkedNodeTypeName', selectedNodeType?.label || '');
                      handleFieldChange('linkedNodeTypeData', selectedNodeType?.data || null);
                    }}
                    options={equipmentNodeTypes}
                    disabled={loadingNodeTypes}
                  />
                  {selectedNode?.data?.linkedNodeTypeName && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        Selected: <span className="font-medium text-foreground">{selectedNode?.data?.linkedNodeTypeName}</span>
                      </p>
                      {selectedNode?.data?.linkedNodeTypeData?.description && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedNode?.data?.linkedNodeTypeData?.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={selectedNode?.data?.status || 'pending'}
                  onChange={(value) => handleFieldChange('status', value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">General Node</label>
                <Input
                  value={selectedNode?.data?.generalNode || ''}
                  onChange={(e) => handleFieldChange('generalNode', e?.target?.value)}
                  placeholder="General node information"
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created by {selectedNode?.data?.createdBy || user?.email || 'Unknown User'}</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <Checkbox
                  id="container-mode"
                  checked={selectedNode?.data?.isContainer || false}
                  onChange={(checked) => handleFieldChange('isContainer', checked)}
                />
                <label htmlFor="container-mode" className="text-sm text-foreground cursor-pointer flex-1">
                  Container Node
                </label>
                <Icon name="Box" size={16} color="var(--color-muted-foreground)" />
              </div>
            </>)
          ) : isLaborNode ? (
            // Labor-specific properties
            (<>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Node Name</label>
                <Input
                  value={selectedNode?.name || ''}
                  onChange={(e) => {
                    onNodeUpdate(selectedNode?.id, {
                      ...selectedNode,
                      name: e?.target?.value
                    });
                  }}
                  placeholder="Enter labor node name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">UID</label>
                <Input
                  value={selectedNode?.data?.uid || selectedNode?.id || ''}
                  placeholder="Auto-generated unique identifier"
                  disabled
                  className="bg-muted/50 cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground mt-1">Auto generated</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Labor Node Type</label>
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={16} color="var(--color-primary)" />
                    <p className="text-xs font-medium text-foreground">Link to Node Library Management</p>
                  </div>
                  <Select
                    value={selectedNode?.data?.linkedNodeTypeId || ''}
                    onChange={(value) => {
                      const selectedNodeType = laborNodeTypes?.find(nt => nt?.value === value);
                      handleFieldChange('linkedNodeTypeId', value);
                      handleFieldChange('linkedNodeTypeName', selectedNodeType?.label || '');
                      handleFieldChange('linkedNodeTypeData', selectedNodeType?.data || null);
                    }}
                    options={laborNodeTypes}
                    disabled={loadingNodeTypes}
                  />
                  {selectedNode?.data?.linkedNodeTypeName && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        Selected: <span className="font-medium text-foreground">{selectedNode?.data?.linkedNodeTypeName}</span>
                      </p>
                      {selectedNode?.data?.linkedNodeTypeData?.description && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedNode?.data?.linkedNodeTypeData?.description}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Select Department</label>
                <Select
                  value={selectedNode?.data?.department || ''}
                  onChange={(value) => handleFieldChange('department', value)}
                  options={[
                    { value: '', label: 'Select Department' },
                    { value: 'production', label: 'Production' },
                    { value: 'assembly', label: 'Assembly' },
                    { value: 'quality-control', label: 'Quality Control' },
                    { value: 'maintenance', label: 'Maintenance' },
                    { value: 'logistics', label: 'Logistics' },
                    { value: 'engineering', label: 'Engineering' },
                    { value: 'fabrication', label: 'Fabrication' },
                    { value: 'welding', label: 'Welding' },
                    { value: 'painting', label: 'Painting' },
                    { value: 'packaging', label: 'Packaging' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Select Worker</label>
                <Select
                  value={selectedNode?.data?.workerId || ''}
                  onChange={(value) => {
                    // Find worker details from the selected value
                    const workerOptions = [
                      { value: 'W001', label: 'John Smith', id: 'W001' },
                      { value: 'W002', label: 'Sarah Johnson', id: 'W002' },
                      { value: 'W003', label: 'Michael Chen', id: 'W003' },
                      { value: 'W004', label: 'Emily Rodriguez', id: 'W004' },
                      { value: 'W005', label: 'David Brown', id: 'W005' },
                      { value: 'W006', label: 'Lisa Anderson', id: 'W006' },
                      { value: 'W007', label: 'James Wilson', id: 'W007' },
                      { value: 'W008', label: 'Maria Garcia', id: 'W008' },
                      { value: 'W009', label: 'Robert Taylor', id: 'W009' },
                      { value: 'W010', label: 'Jennifer Lee', id: 'W010' }
                    ];
                    const selectedWorker = workerOptions?.find(w => w?.value === value);
                    handleFieldChange('workerId', value);
                    handleFieldChange('workerName', selectedWorker?.label || '');
                  }}
                  options={[
                    { value: '', label: 'Select Worker' },
                    { value: 'W001', label: 'John Smith' },
                    { value: 'W002', label: 'Sarah Johnson' },
                    { value: 'W003', label: 'Michael Chen' },
                    { value: 'W004', label: 'Emily Rodriguez' },
                    { value: 'W005', label: 'David Brown' },
                    { value: 'W006', label: 'Lisa Anderson' },
                    { value: 'W007', label: 'James Wilson' },
                    { value: 'W008', label: 'Maria Garcia' },
                    { value: 'W009', label: 'Robert Taylor' },
                    { value: 'W010', label: 'Jennifer Lee' }
                  ]}
                />
              </div>
              {selectedNode?.data?.workerId && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Worker ID</label>
                  <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{selectedNode?.data?.workerName || 'Unknown Worker'}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">ID: {selectedNode?.data?.workerId}</p>
                      </div>
                      <Icon name="User" size={20} color="var(--color-primary)" />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Skill Level</label>
                <Select
                  value={selectedNode?.data?.skillLevel || ''}
                  onChange={(value) => handleFieldChange('skillLevel', value)}
                  options={[
                    { value: '', label: 'Select Skill Level' },
                    { value: 'entry', label: 'Entry Level' },
                    { value: 'intermediate', label: 'Intermediate' },
                    { value: 'advanced', label: 'Advanced' },
                    { value: 'expert', label: 'Expert' },
                    { value: 'master', label: 'Master Craftsman' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Hourly Rate</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={selectedNode?.data?.hourlyRate || 0}
                    onChange={(e) => handleFieldChange('hourlyRate', parseFloat(e?.target?.value) || 0)}
                    placeholder="0.00"
                    min="0"
                    step="0.50"
                    className="pl-7"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">per hour</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Availability Status</label>
                <Select
                  value={selectedNode?.data?.availabilityStatus || ''}
                  onChange={(value) => handleFieldChange('availabilityStatus', value)}
                  options={[
                    { value: '', label: 'Select Availability' },
                    { value: 'available', label: 'Available' },
                    { value: 'assigned', label: 'Assigned' },
                    { value: 'on-break', label: 'On Break' },
                    { value: 'off-duty', label: 'Off Duty' },
                    { value: 'on-leave', label: 'On Leave' },
                    { value: 'training', label: 'In Training' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Labor Attributes</label>
                <textarea
                  value={selectedNode?.data?.laborAttributes || ''}
                  onChange={(e) => handleFieldChange('laborAttributes', e?.target?.value)}
                  placeholder="Enter labor-specific attributes (certifications, specializations, equipment proficiency, etc.)"
                  className="w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">Certifications, specializations, equipment proficiency</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Shift Assignment</label>
                <Select
                  value={selectedNode?.data?.shiftAssignment || ''}
                  onChange={(value) => handleFieldChange('shiftAssignment', value)}
                  options={[
                    { value: '', label: 'Select Shift' },
                    { value: 'morning', label: 'Morning Shift (6AM - 2PM)' },
                    { value: 'afternoon', label: 'Afternoon Shift (2PM - 10PM)' },
                    { value: 'night', label: 'Night Shift (10PM - 6AM)' },
                    { value: 'flexible', label: 'Flexible' }
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Estimated Hours</label>
                <Input
                  type="number"
                  value={selectedNode?.data?.estimatedHours || 0}
                  onChange={(e) => handleFieldChange('estimatedHours', parseFloat(e?.target?.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="0.5"
                />
                <p className="text-xs text-muted-foreground mt-1">Total hours for this task</p>
              </div>
              {selectedNode?.data?.hourlyRate && selectedNode?.data?.estimatedHours ? (
                <div className="p-3 bg-muted/30 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Estimated Labor Cost</span>
                    <span className="text-lg font-bold text-primary">
                      ${(selectedNode?.data?.hourlyRate * selectedNode?.data?.estimatedHours)?.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedNode?.data?.estimatedHours}h × ${selectedNode?.data?.hourlyRate}/h
                  </p>
                </div>
              ) : null}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={selectedNode?.data?.status || 'pending'}
                  onChange={(value) => handleFieldChange('status', value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">General Node</label>
                <Input
                  value={selectedNode?.data?.generalNode || ''}
                  onChange={(e) => handleFieldChange('generalNode', e?.target?.value)}
                  placeholder="General node information"
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created by {selectedNode?.data?.createdBy || user?.email || 'Unknown User'}</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <Checkbox
                  id="container-mode"
                  checked={selectedNode?.data?.isContainer || false}
                  onChange={(checked) => handleFieldChange('isContainer', checked)}
                />
                <label htmlFor="container-mode" className="text-sm text-foreground cursor-pointer flex-1">
                  Container Node
                </label>
                <Icon name="Box" size={16} color="var(--color-muted-foreground)" />
              </div>
            </>)
          ) : isBaseTaskNode ? (
            // Base Task properties panel
            (<>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Node Name</label>
                <Input
                  value={selectedNode?.name || ''}
                  onChange={(e) => {
                    onNodeUpdate(selectedNode?.id, {
                      ...selectedNode,
                      name: e?.target?.value
                    });
                  }}
                  placeholder="Enter task node name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Task Description</label>
                <textarea
                  value={selectedNode?.data?.taskDescription || ''}
                  onChange={(e) => handleFieldChange('taskDescription', e?.target?.value)}
                  placeholder="Enter detailed task description, objectives, and requirements"
                  className="w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  rows={5}
                />
                <p className="text-xs text-muted-foreground mt-1">Describe the task objectives, requirements, and deliverables</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Connected Nodes (Direct Connections Below)</label>
                <div className="space-y-2">
                  {(() => {
                    // Get all connections where this node is the target (nodes connecting TO this node)
                    const directConnections = (window.__workflowConnections || [])?.filter(
                      conn => conn?.target === selectedNode?.id
                    );
                    
                    if (directConnections?.length === 0) {
                      return (
                        <div className="p-4 bg-muted/30 rounded-lg border border-dashed border-border text-center">
                          <Icon name="GitBranch" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">No nodes connected to this node</p>
                        </div>
                      );
                    }
                    
                    // Get source nodes (nodes that connect TO this node)
                    const connectedNodes = directConnections?.map(conn => {
                      const sourceNode = (window.__workflowNodes || [])?.find(n => n?.id === conn?.source);
                      return sourceNode;
                    })?.filter(Boolean);
                    
                    return (
                      <div className="space-y-2">
                        {connectedNodes?.map((node) => (
                          <div
                            key={node?.id}
                            className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border border-border hover:border-primary/50 transition-colors duration-200"
                          >
                            <div
                              className="flex items-center justify-center w-7 h-7 rounded flex-shrink-0"
                              style={{ backgroundColor: `${node?.color}20`, color: node?.color }}
                            >
                              <Icon name={node?.icon || 'Circle'} size={14} />
                            </div>
                            <span className="text-sm font-medium text-foreground truncate flex-1">{node?.name}</span>
                            {node?.data?.status && (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                                node?.data?.status === 'completed' ? 'bg-green-100 text-green-800' :
                                node?.data?.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                                node?.data?.status === 'blocked'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {node?.data?.status}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Link Base Task Node Files</label>
                <div className="space-y-3">
                  <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name="FileText" size={16} color="var(--color-primary)" />
                      <p className="text-xs font-medium text-foreground">Attached Files</p>
                    </div>
                    {selectedNode?.data?.linkedFiles && selectedNode?.data?.linkedFiles?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedNode?.data?.linkedFiles?.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-background rounded border border-border"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Icon name="File" size={14} color="var(--color-muted-foreground)" />
                              <span className="text-xs text-foreground truncate">{file?.name}</span>
                            </div>
                            <button
                              onClick={() => {
                                const updatedFiles = selectedNode?.data?.linkedFiles?.filter((_, i) => i !== index);
                                handleFieldChange('linkedFiles', updatedFiles);
                              }}
                              className="flex-shrink-0 p-1 hover:bg-destructive/10 rounded transition-colors"
                              aria-label="Remove file"
                            >
                              <Icon name="X" size={12} color="var(--color-destructive)" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No files attached</p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => {
                      // Trigger file input
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png';
                      input.onchange = (e) => {
                        const files = Array.from(e?.target?.files || []);
                        const fileData = files?.map(f => ({
                          name: f?.name,
                          size: f?.size,
                          type: f?.type,
                          uploadedAt: new Date()?.toISOString()
                        }));
                        const currentFiles = selectedNode?.data?.linkedFiles || [];
                        handleFieldChange('linkedFiles', [...currentFiles, ...fileData]);
                      };
                      input?.click();
                    }}
                  >
                    <Icon name="Upload" size={16} className="mr-2" />
                    Upload Files
                  </Button>
                  <p className="text-xs text-muted-foreground">Supported: PDF, DOC, XLS, TXT, Images</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={selectedNode?.data?.status || 'pending'}
                  onChange={(value) => handleFieldChange('status', value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                <Select
                  value={selectedNode?.data?.priority || 'medium'}
                  onChange={(value) => handleFieldChange('priority', value)}
                  options={priorityOptions}
                />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created by {selectedNode?.data?.createdBy || user?.email || 'Unknown User'}</p>
              </div>
              <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
                <Checkbox
                  id="container-mode"
                  checked={selectedNode?.data?.isContainer || false}
                  onChange={(checked) => handleFieldChange('isContainer', checked)}
                />
                <label htmlFor="container-mode" className="text-sm text-foreground cursor-pointer flex-1">
                  Container Node
                </label>
                <Icon name="Box" size={16} color="var(--color-muted-foreground)" />
              </div>
            </>)
          ) : (
            // Standard node properties for non-material nodes
            (<>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Duration (hours)</label>
                <Input
                  type="number"
                  value={selectedNode?.data?.duration || 0}
                  onChange={(e) => handleFieldChange('duration', parseFloat(e?.target?.value) || 0)}
                  placeholder="0"
                  min="0"
                  step="0.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Workers Required</label>
                <Input
                  type="number"
                  value={selectedNode?.data?.workers || 0}
                  onChange={(e) => handleFieldChange('workers', parseInt(e?.target?.value) || 0)}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <Select
                  value={selectedNode?.data?.status || 'pending'}
                  onChange={(value) => handleFieldChange('status', value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
                <Select
                  value={selectedNode?.data?.priority || 'medium'}
                  onChange={(value) => handleFieldChange('priority', value)}
                  options={priorityOptions}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="container-mode"
                  checked={selectedNode?.data?.isContainer || false}
                  onChange={(checked) => handleFieldChange('isContainer', checked)}
                />
                <label htmlFor="container-mode" className="text-sm text-foreground cursor-pointer">
                  Container Node
                </label>
              </div>
            </>)
          )}

          <div className="pt-4 border-t border-border">
            <Button
              onClick={handleDeleteNode}
              variant="destructive"
              className="w-full"
              type="button"
            >
              <Icon name="Trash2" size={16} className="mr-2" />
              Delete Node
            </Button>
          </div>
        </div>
      </div>
      <div className="px-4 py-4 border-t border-border space-y-2">
        <Button
          variant="outline"
          fullWidth
          iconName="Copy"
          onClick={() => console.log('Duplicate node:', selectedNode?.id)}
        >
          Duplicate Node
        </Button>
      </div>
      {isMaterialModalOpen && (
        <MaterialSelectionModal
          isOpen={isMaterialModalOpen}
          onClose={() => setIsMaterialModalOpen(false)}
          onSelectMaterial={handleMaterialSelect}
        />
      )}
      {isEquipmentModalOpen && (
        <EquipmentSelectionModal
          isOpen={isEquipmentModalOpen}
          onClose={() => setIsEquipmentModalOpen(false)}
          onSelectEquipment={handleEquipmentSelect}
        />
      )}
    </div>
  );
};

export default PropertiesPanel;